import { Answer } from '../types';

interface KeywordMap {
  [key: string]: string[];
}

interface CareerPath {
  title: string;
  description: string;
  keySkills: string[];
  relatedFields: string[];
  keywords: string[];
  workStyles: string[];
}

const careerPaths: CareerPath[] = [
  {
    title: 'Data Scientist',
    description: 'Analyze complex data sets to help organizations make better decisions',
    keySkills: ['Analytics', 'Programming', 'Statistics', 'Problem Solving'],
    relatedFields: ['Machine Learning', 'Business Intelligence', 'Research'],
    keywords: ['data', 'analysis', 'numbers', 'patterns', 'research', 'solving problems'],
    workStyles: ['analytical', 'detail-oriented', 'methodical']
  },
  {
    title: 'UX/UI Designer',
    description: 'Create intuitive and beautiful digital experiences for users',
    keySkills: ['Design', 'User Research', 'Prototyping', 'Visual Communication'],
    relatedFields: ['Product Design', 'Web Development', 'Digital Marketing'],
    keywords: ['design', 'creative', 'visual', 'art', 'user experience', 'aesthetics'],
    workStyles: ['creative', 'empathetic', 'collaborative']
  },
  {
    title: 'Project Manager',
    description: 'Lead teams and coordinate resources to deliver successful projects',
    keySkills: ['Leadership', 'Organization', 'Communication', 'Problem Solving'],
    relatedFields: ['Program Management', 'Operations', 'Consulting'],
    keywords: ['organize', 'lead', 'coordinate', 'plan', 'manage', 'team'],
    workStyles: ['organized', 'leadership', 'collaborative']
  },
  {
    title: 'Content Creator',
    description: 'Produce engaging content across various digital platforms',
    keySkills: ['Writing', 'Storytelling', 'Social Media', 'Video Production'],
    relatedFields: ['Digital Marketing', 'Journalism', 'Social Media Management'],
    keywords: ['writing', 'creative', 'content', 'stories', 'social media'],
    workStyles: ['creative', 'independent', 'flexible']
  },
  {
    title: 'Software Developer',
    description: 'Build and maintain software applications and systems',
    keySkills: ['Programming', 'Problem Solving', 'Debugging', 'System Design'],
    relatedFields: ['Web Development', 'Mobile Development', 'DevOps'],
    keywords: ['code', 'programming', 'technology', 'building', 'solving problems'],
    workStyles: ['analytical', 'detail-oriented', 'independent']
  },
  {
    title: 'Business Consultant',
    description: 'Help organizations improve their performance and operations',
    keySkills: ['Analysis', 'Strategy', 'Communication', 'Problem Solving'],
    relatedFields: ['Management Consulting', 'Strategy', 'Business Development'],
    keywords: ['business', 'strategy', 'analysis', 'improvement', 'consulting'],
    workStyles: ['analytical', 'leadership', 'collaborative']
  },
  {
    title: 'Digital Marketing Specialist',
    description: 'Create and execute marketing strategies across digital channels',
    keySkills: ['Marketing', 'Analytics', 'Social Media', 'Content Creation'],
    relatedFields: ['Content Marketing', 'SEO', 'Social Media Marketing'],
    keywords: ['marketing', 'digital', 'social media', 'content', 'strategy'],
    workStyles: ['creative', 'analytical', 'collaborative']
  },
  {
    title: 'Healthcare Professional',
    description: 'Provide care and support to patients in various healthcare settings',
    keySkills: ['Patient Care', 'Communication', 'Medical Knowledge', 'Empathy'],
    relatedFields: ['Nursing', 'Medical Practice', 'Healthcare Administration'],
    keywords: ['health', 'care', 'helping others', 'medical', 'patients'],
    workStyles: ['empathetic', 'detail-oriented', 'collaborative']
  }
];

const categoryKeywords: KeywordMap = {
  'analytical': ['data', 'analysis', 'research', 'patterns', 'solving', 'logical', 'systematic'],
  'creative': ['design', 'art', 'writing', 'creative', 'innovative', 'imagination'],
  'social': ['people', 'helping', 'teaching', 'communicating', 'team', 'collaboration'],
  'entrepreneurial': ['business', 'leadership', 'initiative', 'strategy', 'innovation'],
  'technical': ['technology', 'programming', 'coding', 'building', 'engineering'],
  'organizational': ['planning', 'organizing', 'managing', 'coordinating', 'systematic']
};

export function analyzeAnswers(answers: Answer[]): CareerPath[] {
  const userTraits = extractTraits(answers);
  return matchCareers(userTraits);
}

function extractTraits(answers: Answer[]): string[] {
  const traits: Set<string> = new Set();
  
  answers.forEach(answer => {
    const lowerAnswer = answer.response.toLowerCase();
    
    // Check each category's keywords against the answer
    Object.entries(categoryKeywords).forEach(([category, keywords]) => {
      if (keywords.some(keyword => lowerAnswer.includes(keyword))) {
        traits.add(category);
      }
    });
    
    // Add specific work style preferences
    if (lowerAnswer.includes('alone') || lowerAnswer.includes('independent')) {
      traits.add('independent');
    }
    if (lowerAnswer.includes('team') || lowerAnswer.includes('group')) {
      traits.add('collaborative');
    }
    if (lowerAnswer.includes('detail') || lowerAnswer.includes('thorough')) {
      traits.add('detail-oriented');
    }
    if (lowerAnswer.includes('lead') || lowerAnswer.includes('guide')) {
      traits.add('leadership');
    }
  });
  
  return Array.from(traits);
}

function matchCareers(userTraits: string[]): CareerPath[] {
  const matches = careerPaths.map(career => {
    let score = 0;
    const traitMatches = userTraits.filter(trait => 
      career.workStyles.includes(trait) || 
      career.keywords.some(keyword => trait.includes(keyword))
    );
    
    score = traitMatches.length;
    
    return {
      ...career,
      matchScore: score
    };
  });
  
  // Sort by match score and return top matches
  return matches
    .filter(match => match.matchScore > 0)
    .sort((a, b) => b.matchScore - a.matchScore)
    .slice(0, 5);
}